import { Link } from "react-router-dom";
import { Network, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const LAST_UPDATED = "February 5, 2026";

const SUBPROCESSORS = [
  {
    name: "Emergent Cloud",
    purpose: "Application hosting (Kubernetes)",
    data_processed: "All platform data, in transit and at rest",
    location: "United States",
    url: "https://emergent.sh",
  },
  {
    name: "MongoDB Atlas",
    purpose: "Primary database",
    data_processed: "User accounts, lesson responses, business plans",
    location: "United States",
    url: "https://www.mongodb.com/legal/privacy-policy",
  },
  {
    name: "OpenAI",
    purpose: "LemonBot AI coach, voice (TTS), and Whisper STT",
    data_processed: "Chat prompts, lesson context (no PII), audio for transcription",
    location: "United States",
    url: "https://openai.com/policies/privacy-policy",
  },
  {
    name: "SendGrid (Twilio)",
    purpose: "Transactional email — magic links, password reset, parent share",
    data_processed: "Email address, name",
    location: "United States",
    url: "https://www.twilio.com/legal/privacy",
  },
  {
    name: "Sentry",
    purpose: "Error tracking and monitoring (PII fields redacted at source)",
    data_processed: "Error messages, stack traces, user ID (no PII)",
    location: "United States",
    url: "https://sentry.io/privacy/",
  },
  {
    name: "PostHog",
    purpose: "Privacy-respecting product analytics",
    data_processed: "Anonymized page views, feature usage (no PII)",
    location: "United States",
    url: "https://posthog.com/privacy",
  },
  {
    name: "Google Analytics 4",
    purpose: "Aggregated visitor metrics",
    data_processed: "Anonymized IP, page views, referrer",
    location: "United States",
    url: "https://policies.google.com/privacy",
  },
];

export default function SubprocessorsPage() {
  useDocumentMeta({
    title: "Subprocessors | Lemonade Stand Business Plan",
    description:
      "Full vendor transparency list for The Lemonade Stand Business Plan — hosting, AI, database, email, and analytics providers handling student data.",
    keywords:
      "subprocessors, vendor transparency, student data privacy, K-12 educational technology",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex flex-col">
      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14 flex-1 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-6"
          data-testid="subprocessors-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <header className="mb-8">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <Network className="w-3.5 h-3.5" /> Vendor Transparency
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Subprocessors
          </h1>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
          <p className="mt-4 text-gray-700 max-w-2xl">
            We use a small set of vetted service providers to run the Lemonade
            Stand Business Plan. Below is the current, complete list — each is
            bound by contract to handle data per our{" "}
            <Link to="/privacy" className="text-yellow-700 underline">
              Privacy Policy
            </Link>{" "}
            and applicable laws (FERPA, COPPA, GDPR).
          </p>
        </header>

        <section className="space-y-4">
          {SUBPROCESSORS.map((sp) => (
            <article
              key={sp.name}
              className="bg-white ring-1 ring-gray-200 rounded-2xl p-5 shadow-sm"
              data-testid={`subprocessor-${sp.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <h3 className="text-lg font-bold text-gray-900">{sp.name}</h3>
                <a
                  href={sp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-yellow-700 underline font-semibold"
                >
                  Privacy policy ↗
                </a>
              </div>
              <dl className="grid sm:grid-cols-3 gap-2 sm:gap-4 text-sm">
                <div>
                  <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Purpose</dt>
                  <dd className="text-gray-800">{sp.purpose}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Data processed</dt>
                  <dd className="text-gray-800">{sp.data_processed}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Location</dt>
                  <dd className="text-gray-800">{sp.location}</dd>
                </div>
              </dl>
            </article>
          ))}
        </section>

        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 mt-8">
          <p className="text-sm font-semibold text-indigo-900 mb-1">
            Changes to this list
          </p>
          <p className="text-sm text-indigo-900">
            We notify school customers by email at least 30 days before adding
            or removing a subprocessor that handles student data. To subscribe
            to subprocessor change notices, email{" "}
            <a href="mailto:privacy@thewementality.com" className="underline font-medium">
              privacy@thewementality.com
            </a>
            .
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
